import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  TechDebtIssue,
} from '@internal/backstage-plugin-techdebt-common';
import { techDebtApiRef } from '../api';
import { parseEntityRef } from '@backstage/catalog-model';

export type NonDebtCode = 'D2' | 'D3' | 'D4' | 'D5';
export type K1Subtype = 'K1a' | 'K1b';
type WorkKind = 'defect' | 'improvement';

export type DebtTrigger = Exclude<TechDebtIssue['debtTrigger'], undefined>;

export type DebtType = Exclude<TechDebtIssue['debtType'], undefined>;

export type DebtOrigin = Exclude<TechDebtIssue['debtOrigin'], undefined>;

type Option<T extends string> = {
  value: T;
  label: string;
  description: string;
};

export type FlowScreen =
  | 'Q1'
  | 'Q2'
  | 'Q3A'
  | 'Q3B'
  | 'Q4'
  | 'NOT_DEBT'
  | 'K1_SUBTYPE'
  | 'DETAILS'
  | 'REVIEW'
  | 'SUCCESS';

type DecisionAnswers = {
  q1TouchesSystem?: boolean;
  q2WorkKind?: WorkKind;
  q3aNormalBug?: boolean;
  q3bDesignLimitation?: boolean;
  q4Accumulates?: boolean;
};

const nonDebtReason: Record<NonDebtCode, string> = {
  D2: 'This does not require changes to executable artifacts or data structures, so it is not technical debt in this model.',
  D3: 'This is a normal incorrect-functionality bug and should be handled as standard defect work.',
  D4: 'This does not show accumulating cost/risk over time, so it does not match technical debt behavior.',
  D5: 'This is generic feature work and should be handled in normal product planning.',
};

const nonDebtNextStep: Record<NonDebtCode, string> = {
  D2: 'Track this in the normal delivery process, such as Jira tasks tied to planning routines.',
  D3: 'Register this in Jira as a bug and prioritize through regular defect management.',
  D4: 'Treat this as normal improvement work and prioritize in standard planning.',
  D5: 'Register this as a feature/story/epic in Jira and plan it in regular roadmap flow.',
};

export const flowSteps = ['Classify', 'Details', 'Review'];

export const debtTriggerOptions: Option<DebtTrigger>[] = [
  {
    value: 'Incident-driven',
    label: 'Incident-driven',
    description: 'Recurring incidents, outages, or unstable behavior exposed this issue.',
  },
  {
    value: 'Audit/Compliance',
    label: 'Audit/Compliance',
    description: 'An audit finding or compliance gap requires remediation work.',
  },
  {
    value: 'Delivery friction',
    label: 'Delivery friction',
    description: 'Lead time/rework pain is high due to current technical constraints.',
  },
  {
    value: 'EOL/Upgrade',
    label: 'EOL/Upgrade',
    description: 'End-of-life tech or version support pressure triggered this work.',
  },
  {
    value: 'Security finding',
    label: 'Security finding',
    description: 'Security review, scanning, or incident response surfaced this debt.',
  },
  {
    value: 'Performance issue',
    label: 'Performance issue',
    description: 'Latency/throughput/stability constraints now hurt outcomes.',
  },
  {
    value: 'Cost optimization',
    label: 'Cost optimization',
    description: 'Current architecture/design creates avoidable run/change costs.',
  },
  {
    value: 'Other',
    label: 'Other',
    description: 'Use when none of the above capture the primary trigger.',
  },
];

export const debtTypeOptions: Option<DebtType>[] = [
  {
    value: 'Code quality / maintainability',
    label: 'Code quality / maintainability',
    description: 'Duplication, fragile code paths, inconsistent patterns, hard-to-change modules.',
  },
  {
    value: 'Architecture / design limitation',
    label: 'Architecture / design limitation',
    description: 'Structural constraints block delivery speed or solution flexibility.',
  },
  {
    value: 'Data (modeling, duplication, quality pipelines)',
    label: 'Data (modeling, duplication, quality pipelines)',
    description: 'Data models, quality pipelines, or duplication issues drive recurring effort.',
  },
  {
    value: 'Platform / infra / cloud hygiene',
    label: 'Platform / infra / cloud hygiene',
    description: 'Infrastructure/platform hygiene problems create toil, risk, or drift.',
  },
  {
    value: 'Security / compliance',
    label: 'Security / compliance',
    description: 'Control gaps, hardening gaps, or compliance debt requires technical remediation.',
  },
  {
    value: 'Test automation / release reliability',
    label: 'Test automation / release reliability',
    description: 'Test reliability, release process, or CI/CD weaknesses create compounding risk.',
  },
  {
    value: 'Observability (logging/metrics/tracing)',
    label: 'Observability (logging/metrics/tracing)',
    description: 'Insufficient observability increases triage time and operational risk.',
  },
  {
    value: 'Lifecycle / obsolescence (EOL tech, unsupported versions)',
    label: 'Lifecycle / obsolescence (EOL tech, unsupported versions)',
    description: 'Aging/unsupported dependencies or platforms increase risk and delivery friction.',
  },
];

export const debtOriginOptions: Option<DebtOrigin>[] = [
  {
    value: 'Deliberate',
    label: 'Deliberate',
    description: 'A conscious trade-off was made for speed/time or delivery pressure.',
  },
  {
    value: 'Inadvertent',
    label: 'Inadvertent',
    description: 'Debt emerged unintentionally through evolving complexity or overlooked effects.',
  },
  {
    value: 'Unknown',
    label: 'Unknown',
    description: 'Origin is unclear; more discovery is needed.',
  },
];

export const monthlyInterestOptions = [0, 1, 2, 5, 10, 20, 40];

type UseCreateTechDebtFlowOptions = {
  open: boolean;
  initialEntityRef: string;
  context: string;
};

export const useCreateTechDebtFlow = ({
  open,
  initialEntityRef,
}: UseCreateTechDebtFlowOptions) => {
  const techDebtApi = useApi(techDebtApiRef);
  const [screen, setScreen] = useState<FlowScreen>('Q1');
  const [answers, setAnswers] = useState<DecisionAnswers>({});
  const [nonDebtCode, setNonDebtCode] = useState<NonDebtCode | undefined>(undefined);
  const [k1Subtype, setK1Subtype] = useState<K1Subtype | undefined>(undefined);
  const [overrideUsed, setOverrideUsed] = useState(false);
  const [entityRef, setEntityRef] = useState(initialEntityRef);
  const [title, setTitle] = useState('');
  const [debtTrigger, setDebtTrigger] = useState<TechDebtIssue['debtTrigger'] | undefined>(undefined);
  const [debtType, setDebtType] = useState<TechDebtIssue['debtType'] | undefined>(undefined);
  const [debtOrigin, setDebtOrigin] = useState<TechDebtIssue['debtOrigin'] | undefined>(undefined);
  const [monthlyInterestHours, setMonthlyInterestHours] = useState<TechDebtIssue['monthlyInterestHours'] | undefined>(undefined);
  const [principalHours, setPrincipalHours] = useState<TechDebtIssue['principalHours'] | undefined>(undefined);
  const [interestStatement, setInterestStatement] = useState<TechDebtIssue['interestStatement'] | undefined>(undefined);
  const [businessValue, setBusinessValue] = useState<TechDebtIssue['businessValue'] | undefined>(undefined);
  const [timeCriticality, setTimeCriticality] = useState<TechDebtIssue['timeCriticality'] | undefined>(undefined);
  const [riskReductionOpportunity, setRiskReductionOpportunity] = useState<TechDebtIssue['riskReductionOpportunity'] | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [createdId, setCreatedId] = useState<string | undefined>(undefined);

  const reset = useCallback(() => {
    setScreen('Q1');
    setAnswers({});
    setNonDebtCode(undefined);
    setK1Subtype(undefined);
    setOverrideUsed(false);
    setEntityRef(initialEntityRef);
    setTitle('');
    setDebtTrigger(undefined);
    setDebtType(undefined);
    setDebtOrigin(undefined);
    setMonthlyInterestHours(undefined);
    setPrincipalHours(undefined);
    setInterestStatement('');
    setBusinessValue(undefined);
    setTimeCriticality(undefined);
    setRiskReductionOpportunity(undefined);
    setIsSubmitting(false);
    setSubmitError(undefined);
    setCreatedId(undefined);
  }, [initialEntityRef]);

  useEffect(() => {
    if (!open) {
      return;
    }

    reset();
  }, [open, reset]);

  const activeStep = useMemo(() => {
    if (screen === 'DETAILS') {
      return 1;
    }
    if (screen === 'REVIEW' || screen === 'SUCCESS') {
      return 2;
    }
    return 0;
  }, [screen]);

  useEffect(() => {
    if (debtType) {
      return;
    }

    if (k1Subtype === 'K1a') {
      setDebtType('Code quality / maintainability');
      return;
    }

    if (k1Subtype === 'K1b') {
      setDebtType('Architecture / design limitation');
    }
  }, [debtType, k1Subtype]);

  const canContinueFromDetails =
    title.trim().length > 0 &&
    Boolean(debtTrigger) &&
    Boolean(debtType) &&
    Boolean(debtOrigin) &&
    monthlyInterestHours !== undefined &&
    principalHours !== undefined &&
    businessValue !== undefined &&
    timeCriticality !== undefined &&
    riskReductionOpportunity !== undefined;

  const toNotDebt = (code: NonDebtCode) => {
    setNonDebtCode(code);
    setScreen('NOT_DEBT');
  };

  const answerQ1 = (touchesSystem: boolean) => {
    setAnswers(current => ({ ...current, q1TouchesSystem: touchesSystem }));
    if (!touchesSystem) {
      toNotDebt('D2');
      return;
    }
    setScreen('Q2');
  };

  const answerQ2 = (workKind: WorkKind) => {
    setAnswers(current => ({ ...current, q2WorkKind: workKind }));
    setScreen(workKind === 'defect' ? 'Q3A' : 'Q3B');
  };

  const answerQ3A = (normalBug: boolean) => {
    setAnswers(current => ({ ...current, q3aNormalBug: normalBug }));
    if (normalBug) {
      toNotDebt('D3');
      return;
    }
    setScreen('Q4');
  };

  const answerQ3B = (designLimitation: boolean) => {
    setAnswers(current => ({ ...current, q3bDesignLimitation: designLimitation }));
    if (!designLimitation) {
      toNotDebt('D5');
      return;
    }
    setScreen('Q4');
  };

  const answerQ4 = (accumulates: boolean) => {
    setAnswers(current => ({ ...current, q4Accumulates: accumulates }));
    if (!accumulates) {
      toNotDebt('D4');
      return;
    }
    setNonDebtCode(undefined);
    setScreen('K1_SUBTYPE');
  };

  const continueFromNotDebt = () => {
    setOverrideUsed(true);
    setScreen('DETAILS');
  };

  const onBack = () => {
    if (screen === 'Q2') {
      setScreen('Q1');
      return;
    }
    if (screen === 'Q3A' || screen === 'Q3B') {
      setScreen('Q2');
      return;
    }
    if (screen === 'Q4') {
      setScreen(answers.q2WorkKind === 'defect' ? 'Q3A' : 'Q3B');
      return;
    }
    if (screen === 'NOT_DEBT') {
      if (nonDebtCode === 'D2') {
        setScreen('Q1');
        return;
      }
      if (nonDebtCode === 'D3' || nonDebtCode === 'D5') {
        setScreen(answers.q2WorkKind === 'defect' ? 'Q3A' : 'Q3B');
        return;
      }
      setScreen('Q4');
      return;
    }
    if (screen === 'K1_SUBTYPE') {
      setScreen('Q4');
      return;
    }
    if (screen === 'DETAILS') {
      setScreen(overrideUsed ? 'NOT_DEBT' : 'K1_SUBTYPE');
      return;
    }
    if (screen === 'REVIEW') {
      setScreen('DETAILS');
    }
  };

  const onNextFromDetails = () => {
    if (!canContinueFromDetails) {
      return;
    }
    setScreen('REVIEW');
  };

  const continueFromK1Subtype = () => {
    if (!k1Subtype) {
      return;
    }
    setScreen('DETAILS');
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      if (!entityRef.trim()) {
        throw new Error('Entity Ref is required to create a tech debt issue');
      }

      const createInput: TechDebtIssue = {
        title: title.trim(),
        entityRef: parseEntityRef(entityRef.trim()),
        debtTrigger,
        debtType,
        debtOrigin,
        monthlyInterestHours,
        principalHours,
        interestStatement: interestStatement?.trim() || undefined,
        businessValue,
        timeCriticality,
        riskReductionOpportunity,
        createdAt: new Date().toISOString(),
      };

      const data = (await techDebtApi.createIssue(createInput)) as Partial<TechDebtIssue>;

      setCreatedId(data?.key);
      setScreen('SUCCESS');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create tech debt');
    } finally {
      setIsSubmitting(false);
    }
  };

  const classificationCode = nonDebtCode ?? 'K1';

  return {
    state: {
      screen,
      activeStep,
      answers,
      nonDebtCode,
      k1Subtype,
      overrideUsed,
      classificationCode,
      classificationReason: nonDebtCode ? nonDebtReason[nonDebtCode] : 'This item affects executable/data artifacts and is expected to accumulate cost/risk over time.',
      classificationNextStep: nonDebtCode
        ? nonDebtNextStep[nonDebtCode]
        : 'Track and prioritize this as technical debt work.',
      entityRef,
      title,
      debtTrigger,
      debtType,
      debtOrigin,
      monthlyInterestHours,
      principalHours,
      interestStatement,
      businessValue,
      timeCriticality,
      riskReductionOpportunity,
      isSubmitting,
      submitError,
      createdId,
      canContinueFromDetails,
    },
    actions: {
      reset,
      answerQ1,
      answerQ2,
      answerQ3A,
      answerQ3B,
      answerQ4,
      setK1Subtype,
      continueFromNotDebt,
      setEntityRef,
      setTitle,
      setDebtTrigger,
      setDebtType,
      setDebtOrigin,
      setMonthlyInterestHours,
      setPrincipalHours,
      setInterestStatement,
      setBusinessValue,
      setTimeCriticality,
      setRiskReductionOpportunity,
      onBack,
      onNextFromDetails,
      continueFromK1Subtype,
      onSubmit,
    },
  };
};
